import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, LogIn } from 'lucide-react';
import { apiRequest } from '@/api/client';
import { cn } from '@/lib/utils';

/**
 * Unified login selector (client + driver + admin).
 * @param {{ initialRole?: 'driver' | 'admin' | 'client' }} props
 */
const StaffPortalLogin = ({ initialRole = 'driver' }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [role, setRole] = useState(() => {
    const saved = localStorage.getItem('portal_login_tab');
    const q = searchParams.get('role');
    if (q === 'admin' || q === 'driver' || q === 'client') return q;
    if (saved === 'admin' || saved === 'driver' || saved === 'client') return saved;
    if (initialRole === 'admin' || initialRole === 'client') return initialRole;
    return 'driver';
  });

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [sendingReset, setSendingReset] = useState(false);

  const setRoleAndParams = useCallback(
    (r) => {
      setRole(r);
      setPassword('');
      localStorage.setItem('portal_login_tab', r);
      if (r === 'driver') setSearchParams({});
      if (r === 'admin') setSearchParams({ role: 'admin' });
      if (r === 'client') setSearchParams({ role: 'client' });
    },
    [setSearchParams]
  );

  useEffect(() => {
    localStorage.setItem('portal_login_tab', role);
  }, [role]);

  const handleAdminLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    const result = await apiRequest('/api/auth/admin/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });

    setLoading(false);

    if (!result.ok) {
      toast.error(result.error || t('admin_login_toast_error'));
      return;
    }

    const { access_token, user } = result.data;
    localStorage.setItem('admin_token', access_token);
    localStorage.setItem('admin_user', JSON.stringify(user));
    localStorage.setItem('admin_id', user.id);
    toast.success(t('admin_login_toast_success'));
    navigate('/admin/dashboard');
  };

  const handleDriverLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    const result = await apiRequest('/api/auth/driver/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });

    setLoading(false);

    if (!result.ok) {
      const raw = result.error || t('driver_login_error');
      const isAuthFailure =
        raw === 'Invalid credentials' ||
        /invalid credentials|body is already used|clone.*response/i.test(String(raw));
      toast.error(isAuthFailure ? t('invalid_credentials') : raw);
      return;
    }

    const { access_token, driver } = result.data;
    localStorage.setItem('driver_token', access_token);
    localStorage.setItem('driver_data', JSON.stringify(driver));
    toast.success(t('driver_login_welcome'));
    navigate('/driver/dashboard');
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setSendingReset(true);

    const result = await apiRequest('/api/auth/driver/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email: forgotEmail }),
    });

    setSendingReset(false);

    if (!result.ok) {
      toast.error(result.error || t('driver_forgot_error'));
      return;
    }

    const data = result.data;
    if (data.email_sent !== true) {
      toast.warning(t('driver_forgot_email_not_sent'));
    } else {
      toast.success(t('driver_forgot_success'));
    }
    setShowForgotPassword(false);
    setForgotEmail('');
  };

  const handleClientLogin = async (e) => {
    e.preventDefault();
    localStorage.setItem('portal_login_tab', 'client');
    setLoading(true);

    const result = await apiRequest('/api/auth/client/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });

    setLoading(false);

    if (!result.ok) {
      toast.error(result.error || t('client_login_error'));
      return;
    }

    const data = result.data;
    localStorage.setItem('session_token', data.access_token);
    localStorage.setItem('user', JSON.stringify(data.user));
    toast.success(t('client_login_welcome'));

    if (data.first_login) {
      try {
        sessionStorage.setItem('client_pending_first_password_change', '1');
      } catch {
        /* ignore */
      }
      navigate('/change-password');
    } else {
      navigate('/dashboard');
    }
  };

  const onSubmit = role === 'admin' ? handleAdminLogin : role === 'driver' ? handleDriverLogin : handleClientLogin;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#0a3042] via-[#0A4F6E] to-[#0a3042] px-3 sm:px-4 py-6 sm:py-8">
      <div className="w-full max-w-lg">
        <Button
          type="button"
          variant="ghost"
          className="text-white hover:bg-white/10 mb-4 sm:mb-5"
          onClick={() => navigate('/')}
          data-testid="back-to-home-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('admin_back_to_home')}
        </Button>

        <Card className="border-0 shadow-2xl overflow-hidden">
          <CardHeader className="text-center pb-4 pt-6 space-y-2 bg-white">
            <div className="flex items-center justify-center gap-3 mb-1">
              <img
                src="/car-icon.png"
                alt={t('app_logo_alt')}
                className="h-11 w-11 sm:h-12 sm:w-12 rounded-xl"
              />
              <span className="text-3xl sm:text-4xl font-bold text-[#0A4F6E]">AutoGo</span>
            </div>
            <CardTitle className="text-2xl sm:text-3xl font-bold text-[#0A4F6E]">
              {t('staff_portal_title')}
            </CardTitle>
            <CardDescription className="text-base text-gray-600">
              {t('staff_portal_subtitle')}
            </CardDescription>

            <div
              className="flex rounded-xl border-2 border-[#0A4F6E]/15 p-1 bg-gray-50 mt-4"
              role="tablist"
              aria-label={t('staff_portal_subtitle')}
            >
              <button
                type="button"
                role="tab"
                aria-selected={role === 'client'}
                className={cn(
                  'flex-1 flex items-center justify-center gap-2 rounded-lg py-2.5 text-sm font-semibold transition-all duration-200',
                  role === 'client'
                    ? 'bg-[#39FF77] text-[#0A4F6E] shadow-md'
                    : 'text-gray-600 hover:text-[#0A4F6E]'
                )}
                onClick={() => setRoleAndParams('client')}
                data-testid="staff-tab-client"
              >
                {t('client_portal')}
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={role === 'driver'}
                className={cn(
                  'flex-1 flex items-center justify-center gap-2 rounded-lg py-2.5 text-sm font-semibold transition-all duration-200',
                  role === 'driver'
                    ? 'bg-[#39FF77] text-[#0A4F6E] shadow-md'
                    : 'text-gray-600 hover:text-[#0A4F6E]'
                )}
                onClick={() => setRoleAndParams('driver')}
              >
                {t('staff_portal_tab_driver')}
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={role === 'admin'}
                className={cn(
                  'flex-1 flex items-center justify-center gap-2 rounded-lg py-2.5 text-sm font-semibold transition-all duration-200',
                  role === 'admin'
                    ? 'bg-[#39FF77] text-[#0A4F6E] shadow-md'
                    : 'text-gray-600 hover:text-[#0A4F6E]'
                )}
                onClick={() => setRoleAndParams('admin')}
              >
                {t('staff_portal_tab_admin')}
              </button>
            </div>

            <p className="text-sm font-medium text-[#0A4F6E]/80 pt-1">
              {role === 'admin'
                ? t('staff_portal_admin_banner')
                : role === 'driver'
                  ? t('driver_portal')
                  : t('client_portal')}
            </p>
            <p className="text-xs text-gray-500 -mt-1">
              {role === 'admin'
                ? t('staff_portal_admin_desc')
                : role === 'driver'
                  ? t('sign_in_to_view_services')
                  : t('sign_in_to_manage')}
            </p>
          </CardHeader>

          <CardContent className="bg-white pt-2 pb-8">
            <form onSubmit={onSubmit} className="space-y-5" data-testid="staff-login-form">
              {role === 'client' ? (
                <>
              <div className="space-y-2">
                <Label htmlFor="staff-email">{t('email_address')}</Label>
                <Input
                  id="staff-email"
                  type="email"
                  placeholder={t('email_placeholder')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="h-11"
                  autoComplete="email"
                  data-testid="client-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="staff-password">{t('password')}</Label>
                <Input
                  id="staff-password"
                  type="password"
                  placeholder={t('ph_enter_password')}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="h-11"
                  autoComplete="current-password"
                  data-testid="client-password-input"
                />
              </div>
              <div className="text-right -mt-1">
                <button
                  type="button"
                  onClick={() => navigate('/forgot-password')}
                  className="text-sm text-[#0A4F6E] hover:text-[#39FF77] underline underline-offset-2"
                  data-testid="client-forgot-password-btn"
                >
                  {t('forgot_password')}
                </button>
              </div>
              <Button
                type="submit"
                className="w-full h-11 mt-1 bg-gradient-to-r from-[#39FF77] to-[#2FD767] text-[#0A4F6E] hover:opacity-95 font-semibold shadow-lg"
                disabled={loading}
                data-testid="client-login-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {t('loading')}
                  </>
                ) : (
                  <>
                    <LogIn className="w-4 h-4 mr-2" />
                    {t('sign_in')}
                  </>
                )}
              </Button>
                </>
              ) : (
                <>
              <div className="space-y-2">
                <Label htmlFor="staff-email">{t('email_address')}</Label>
                <Input
                  id="staff-email"
                  type="email"
                  placeholder={role === 'admin' ? t('ph_email_example') : t('email_placeholder')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="h-11"
                  autoComplete="email"
                  data-testid={role === 'admin' ? 'admin-email-input' : 'driver-email-input'}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="staff-password">{t('password')}</Label>
                <Input
                  id="staff-password"
                  type="password"
                  placeholder={t('ph_enter_password')}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="h-11"
                  autoComplete="current-password"
                  data-testid={role === 'admin' ? 'admin-password-input' : 'driver-password-input'}
                />
              </div>

              <Button
                type="submit"
                className="w-full h-11 bg-gradient-to-r from-[#39FF77] to-[#2FD767] text-[#0A4F6E] hover:opacity-95 font-semibold shadow-lg"
                disabled={loading}
                data-testid={role === 'admin' ? 'admin-login-btn' : 'driver-login-btn'}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {role === 'admin' ? t('admin_signing_in') : t('loading')}
                  </>
                ) : (
                  <>
                    <LogIn className="w-4 h-4 mr-2" />
                    {role === 'admin' ? t('admin_sign_in') : t('sign_in')}
                  </>
                )}
              </Button>

              {role === 'driver' ? (
                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => setShowForgotPassword(true)}
                    className="text-sm text-[#0A4F6E] hover:text-[#39FF77] underline underline-offset-2"
                    data-testid="driver-forgot-password-btn"
                  >
                    {t('forgot_password')}
                  </button>
                </div>
              ) : (
                <p className="text-center text-xs text-gray-500">
                  Forgot your password? Use the Driver tab to recover it.
                </p>
              )}
                </>
              )}
            </form>
          </CardContent>
        </Card>

        <Dialog open={showForgotPassword} onOpenChange={setShowForgotPassword}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t('forgot_title')}</DialogTitle>
              <DialogDescription>{t('driver_forgot_description')}</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="forgot-email">{t('forgot_email_label')}</Label>
                <Input
                  id="forgot-email"
                  type="email"
                  placeholder={t('ph_email_driver')}
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  required
                />
              </div>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowForgotPassword(false);
                    setForgotEmail('');
                  }}
                  className="flex-1"
                >
                  {t('cancel')}
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-[#36e961] hover:bg-[#2dd156] text-[#0a3042]"
                  disabled={sendingReset}
                >
                  {sendingReset ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {t('driver_sending')}
                    </>
                  ) : (
                    t('driver_forgot_submit')
                  )}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default StaffPortalLogin;
